import Api from '../../../services/Api';
import StoreFieldInSession from '../../../decorators/StoreFieldInSession';
import Observable from '../../../decorators/Observable';
import AbstractSessionModel from '../../../models/AbstractSessionModel';
import Action from '../../../decorators/Action';
import BusinessWizardData from './BusinessWizardData';
import { IWizardDataModel } from '../../../models/IWizardDataModel';
import WizardSessionData from '../../../services/WizardSessionData';
import { runInAction } from 'mobx';

export interface IBusiness {
  id: string;
  name: string;
  address: string;
  street: string;
  state: string;
  zipCode: string;
  city: string;
  country: string;
  primaryType: string;
  primaryTypeDisplayName: string;
  category?: string;
  phoneNumber: string;
  website: string;
  domain_type: string;
  picture: string[];
  rating: number;
  status: string;
  openingHours: string[];
  openingHoursFormatted: {
    [key: string]: {
      fromTime: string;
      isClosed: boolean;
      toTime: string;
    }[];
  };
}

class BusinessModel extends AbstractSessionModel implements IWizardDataModel {
  @StoreFieldInSession()
  @Observable()
  public companyName: string = '';

  @StoreFieldInSession()
  @Observable()
  public zipOrCity: string = '';
  @Action()
  setCompanyName(name: string) {
    this.companyName = name;
  }

  @Action()
  setZipOrCity(zipOrCity: string) {
    this.zipOrCity = zipOrCity;
  }
  @StoreFieldInSession()
  @Observable()
  public businessList: IBusiness[] = [];

  @StoreFieldInSession()
  @Observable()
  public businessSearchSource: 'places' | 'gmb' = 'places';

  @StoreFieldInSession()
  @Observable()
  public gmbFallbackAttempted: boolean = false;

  @StoreFieldInSession()
  @Observable()
  public manualGmbSearchTriggered: boolean = false;

  @Observable()
  public searchingMoreCompanies: boolean = false;

  @StoreFieldInSession()
  @Observable()
  public selectedBusinessId: string | null = null;

  /**
   * Used to show NotFoundBusinessPanel if the search has been made and the business has not been found
   */
  @StoreFieldInSession()
  @Observable()
  public searchHasBeenMade = false;

  /**
   * Show the business form in NotFoundBusinessPanel after the button is clicked
   */
  @StoreFieldInSession()
  @Observable()
  public showBusinessNotFoundForm = false;

  /**
   * Indicate if the user has not confirmed the business website and want to change it
   */
  @Observable()
  @StoreFieldInSession()
  public yourBusinessWebsiteButtonActivated = false;

  /**
   * This is the category name coming from the google scrape api
   */
  @Observable()
  @StoreFieldInSession()
  public selectedCategoryName = null;

  @Observable()
  @StoreFieldInSession()
  public selectedBusinessWebsite = null;

  @Observable()
  @StoreFieldInSession()
  public selectedBusinessDomainType = null;

  /**
   * The wizard data used in the end for generating the preview
   */
  public businessWizardData = new BusinessWizardData();

  //////////////////////////////////////////
  // Loading indicators
  //////////////////////////////////////////

  /**
   * Flag when we make search for the business in google place api
   */
  @Observable()
  public businessLoading = false;

  /**
   * Show loading indicator when we scrape the business data
   */
  @Observable()
  public isScrapeBusinessDataLoading = false;

  @Observable()
  public startLoadingCategoryDataForId = '';

  @Observable()
  public isCategoryFoundModalVisible = false;

  @Observable()
  public isCategoryNotFoundModalVisible = false;

  @Observable()
  public isWebsitePopupVisible = false;

  // promises
  public categoryNotFoundModalFinishedConfirmation: any = null;
  public categoryFoundModalFinishedConfirmation: any = null;
  public websitePopupFinishedConfirmation: any = null;

  @Observable()
  public movingToNextStepLoading = false;

  @StoreFieldInSession()
  @Observable()
  public manualCompanyData: any = null;

  constructor() {
    super();
    super.initFields();
  }

  @Action()
  setDomainType(domainType: string | null) {
    this.selectedBusinessDomainType = domainType;
  }

  @Action()
  setMovingToNextStepLoading(loading: boolean) {
    this.movingToNextStepLoading = loading;
  }

  @Action()
  openCategoryNotFoundModal(): Promise<any> {
    this.isCategoryNotFoundModalVisible = true;

    return new Promise<any>((resolve) => {
      this.categoryNotFoundModalFinishedConfirmation = (data) => {
        resolve(data);
        runInAction(() => {
          this.isCategoryNotFoundModalVisible = false;
        });
      };
    });
  }

  @Action()
  openCategoryFoundModal(): Promise<any> {
    this.isCategoryFoundModalVisible = true;

    return new Promise<any>((resolve) => {
      this.categoryFoundModalFinishedConfirmation = (data) => {
        resolve(data);
        runInAction(() => {
          this.isCategoryFoundModalVisible = false;
        });
      };
    });
  }

  @Action()
  openWebsitePopup(): Promise<any> {
    this.isWebsitePopupVisible = true;
    return new Promise<any>((resolve) => {
      this.websitePopupFinishedConfirmation = (data) => {
        resolve(data);
      };
    });
  }

  @Action()
  closeWebsitePopup() {
    this.isWebsitePopupVisible = false;
  }

  @Action()
  setSelectedCategory(category: string | null) {
    this.selectedCategoryName = category;
  }

  @Action()
  setSelectedBusinessWebsite(website: string | null) {
    this.selectedBusinessWebsite = website;
  }

  @Action()
  setSelectedBusinessDomainType(domain_type: string | null) {
    this.selectedBusinessDomainType = domain_type;
  }

  @Action()
  setSelectedBusinessId(id: string) {
    this.selectedBusinessId = id;
  }

  @Action()
  setStartLoadingCategoryDataForId(id: string) {
    this.startLoadingCategoryDataForId = id;
  }

  @Action()
  setBusinessDataLoading(loading: boolean) {
    this.isScrapeBusinessDataLoading = loading;
  }

  @Action()
  activateYourBusinessWebsiteButton(flag: boolean) {
    this.yourBusinessWebsiteButtonActivated = flag;
  }

  @Action()
  changeShowBusinessNotFoundFormFlag(show: boolean) {
    this.showBusinessNotFoundForm = show;
  }

  @Action()
  setBusinessList(businessList: any) {
    this.businessList = businessList;
  }

  @Action()
  setBusinessLoading(loading: boolean) {
    this.businessLoading = loading;
  }

  @Action()
  setSearchHasBeenMade(processed: boolean) {
    this.searchHasBeenMade = processed;
  }

  @Action()
  resetSelectedBusiness() {
    this.searchHasBeenMade = false;
    this.selectedBusinessId = null;
  }

  @Action()
  async findBusinessOnGoogle(businessName: string) {
    this.setBusinessLoading(true);
    this.setBusinessSearchSource('places');
    this.setManualGmbSearchTriggered(false);
    let data = await Api.findBusinessOnGoogle(businessName);

    let _slice = data?.data?.slice(0, 6);

    this.setBusinessList(_slice || []);
    this.setBusinessLoading(false);
    this.setSearchHasBeenMade(true);
    this.setGmbFallbackAttempted(false);
  }

  @Action()
  async findBusinessOnGMB(businessName: string) {
    this.searchingMoreCompanies = true;
    try {
      const resp: any = await Api.findBusinessOnGMB(businessName);
      this.setBusinessSearchSource('gmb');
      this.setGmbFallbackAttempted(true);
      let _slice = resp?.data?.slice(0, 6);
      if (_slice) {
        _slice = _slice.map((item, idx) => ({
          ...item,
          id:
            item.id ||
            `gmb_${idx}_${item.phoneNumber || ''}_${item.name || ''}_${item.address || ''}`
        }));
      }
      this.setBusinessList(_slice || []);
    } finally {
      this.searchingMoreCompanies = false;
    }
  }

  @Action()
  setBusinessSearchSource(source: 'places' | 'gmb') {
    this.businessSearchSource = source;
  }

  @Action()
  setGmbFallbackAttempted(flag: boolean) {
    this.gmbFallbackAttempted = flag;
  }

  @Action()
  setManualGmbSearchTriggered(flag: boolean) {
    this.manualGmbSearchTriggered = flag;
  }

  buildSearchPath(companyName: string, zipOrCity: string) {
    let searchPath = companyName;
    if (zipOrCity && zipOrCity.trim() !== '') {
      searchPath += ' zip:' + zipOrCity;
    }
    return searchPath;
  }

  getBussinessById(id: string) {
    return this.businessList.find((business) => business.id === id);
  }

  getSelectedBusiness() {
    return this.getBussinessById(this.selectedBusinessId);
  }

  getWizardData(): any {
    return this.businessWizardData.getWizardData();
  }

  getSessionKey(): string {
    return 'BusinessModel';
  }
}

const model = new BusinessModel();
WizardSessionData.addModel(model);
export default model;
